<template>
    <div id="vehicle-edit-view" class="view form-view">

        <div class="form-box">
            <v-layout>
                <v-flex md12>
                    <v-layout row>
                        <v-flex md4>
                            <h2>Edit vehicle</h2>
                        </v-flex>
                    </v-layout>
                </v-flex>
            </v-layout>
        </div>

        <vehicle-form :handler="editVehicle" :form="this.form" :fleet-uuid="this.$route.params.fleetUuid"></vehicle-form>

        <br /><br />

        <div class="form" v-if="hasAllRoles(['DriverView', 'DriverUpdate'])">
            <v-form
                ref="form"
                v-model="valid"
                lazy-validation
            >
                <v-layout>
                    <v-flex xs12>
                        <v-layout row>
                            <v-flex md12>
                                <div class="content-wrapper">
                                    <div class="info-block">
                                        Choose driver
                                    </div>
                                </div>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex md6>
                                <div class="content-wrapper">
                                    <v-layout row>
                                        <v-flex xs4>
                                            <div class="label text-xs-right">
                                                <span class="text-xs-right">Driver <span class="required">*</span></span>
                                            </div>
                                        </v-flex>
                                        <v-flex xs8>
                                            <driver-autocomplete v-model="formDriver.driver" :fleet="form.fleet" :vehicleId="vehicleUuid" :isFreeDriver="true"></driver-autocomplete>
                                        </v-flex>
                                    </v-layout>
                                </div>
                            </v-flex>
                        </v-layout>
                        <v-layout>
                            <v-flex md6 class="">
                                <div class="content-wrapper text-xs-right">
                                    <sp-button @click.native="validate" text="Save" icon="download"></sp-button>
                                </div>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-form>
        </div>

        <div class="devices-table" v-if="hasAllRoles(['DeviceView'])">
            <devices-table :vehicle-id="vehicleUuid"></devices-table>
        </div>
        <sp-loader v-if="isLoading"></sp-loader>
    </div>
</template>

<script>
import VehicleForm from '@/views/Vehicles/components/VehicleForm'
import toast from '@/plugins/izitoast'
import SpButton from '@/components/forms/Button'
import DevicesTable from '@/views/Devices/components/DevicesTable'
import DriverAutocomplete from '@/views/Drivers/components/DriverAutocomplete'
import VehicleApi from '@/api/VehicleApi'
import DriversApi from '@/api/DriversApi'
import FleetApi from '@/api/FleetApi'
import router from '@/router/routes'
import { calculateSpeedOrDistanceUnit } from '@/helpers/Utils'
import { mapGetters } from 'vuex'
import SpLoader from '@/components/atoms/Loader'

const fleetApi = new FleetApi()
const vehicleApi = new VehicleApi()
const driversApi = new DriversApi()

export default {
    name: 'VehicleEdit',
    created () {
        this.fleetUuid = this.$route.params.fleetUuid
        this.vehicleUuid = this.$route.params.vehicleUuid
        this.getVehicle()
    },
    components: {
        DriverAutocomplete,
        DevicesTable,
        VehicleForm,
        SpButton,
        SpLoader
    },
    data () {
        return {
            valid: false,
            fleetUuid: null,
            vehicleUuid: null,
            driver: null,
            isLoading: false,
            form: {
                registration: '',
                manufacturer: '',
                model: '',
                fuelType: null,
                yearOfManufacture: '',
                mileage: 0,
                engineSize: null,
                vehicleType: null,
                chassisNumber: '',
                hexColor: '#AED51B',
                fleet: null
            },
            formDriver: {
                driver: null
            }
        }
    },
    computed: {
        ...mapGetters('authModule', [
            'getSpeedUnit'
        ])
    },
    methods: {
        async editVehicle (form) {
            const response = await vehicleApi.editVehicle(
                this.vehicleUuid,
                form.fleet.id,
                form.registration,
                form.manufacturer,
                form.model,
                form.fuelType,
                form.yearOfManufacture,
                form.mileage,
                form.engineSize,
                form.vehicleType,
                form.chassisNumber,
                form.hexColor
            )

            if (!response.isSuccess) {
                return
            }

            const hasFleetChanged = this.fleetUuid !== form.fleet.id;

            if (hasFleetChanged) {
                this.$router.push(`/settings/fleets/${form.fleet.id}/vehicle/edit/${this.vehicleUuid}`)
            }
            toast.success('Vehicle successfully updated.')
        },
        async getVehicle () {
            this.isLoading = true

            const response = await vehicleApi.getVehicle(this.vehicleUuid)
            if (this.fleetUuid) {
                const currentFleet = await fleetApi.getFleet(this.fleetUuid)
                this.form.fleet = currentFleet.payload
            } else {
                this.form.fleet = null
            }

            if (!response.isSuccess) {
                this.isLoading = false
                return
            }

            this.form.registration = response.payload.registrationNumber
            this.form.manufacturer = response.payload.manufacturer
            this.form.model = response.payload.model
            this.form.fuelType = response.payload.fuelType
            this.form.yearOfManufacture = response.payload.yearOfManufacture
            this.form.mileage = 0 === this.getSpeedUnit ? response.payload.mileage : calculateSpeedOrDistanceUnit(response.payload.mileage, 'mi')
            this.form.engineSize = response.payload.engineSize
            this.form.vehicleType = response.payload.type
            this.form.chassisNumber = response.payload.chassisNumber

            if (response.payload.driver) {
                this.driver = response.payload.driver
                this.formDriver.driver = response.payload.driver
            }
            this.isLoading = false
        },
        async validate () {
            this.valid = this.$refs.form.validate()
            if (!this.valid) {
                return false
            }

            if (this.formDriver.driver || null) {
                const response = await driversApi.assignDriverToVehicle(this.formDriver.driver.id, this.vehicleUuid)

                if (!response.isSuccess) {
                    return
                }

                this.driver = this.formDriver.driver
                toast.success('The driver has been assigned.')
            } else {
                const response = await driversApi.unassignDriverFromVehicle(this.driver.id)
                if (!response.isSuccess) {
                    return
                }

                toast.success('The driver has been unassigned.')
            }
        }
    }
}
</script>

<style scoped lang="scss">
#spillard-theme {
    .v-btn:not(:last-child) {
        margin: 0 10px 0 0;
    }
}

.close {
    position: absolute;
    top: 18px;
    right: 20px;
    cursor: pointer;

    &:hover {
        opacity: 0.7;
    }
}
</style>
